import type { STATEPREFIXTYPE } from './prefix';

interface IPropActions {
	dataAction: string;
	dataLoading: string;
	dataError: string;
}

const generateActionTypes = <K extends string>(action: K[]): Record<K, IPropActions> =>
	action.reduce(
		(acc, i) => {
			acc[i] = {
				dataAction: i,
				dataLoading: `${i}Loading`,
				dataError: `${i}Error`,
			};
			return acc;
		},
		{} as Record<K, IPropActions>
	);

const generateActionTypesNew = <K extends string>(type: STATEPREFIXTYPE, action: K[]): Record<K, IPropActions> =>
	action.reduce(
		(acc, i) => {
			acc[i] = {
				dataAction: `${type}_${i}`,
				dataLoading: `${type}_${i}Loading`,
				dataError: `${type}_${i}Error`,
			};
			return acc;
		},
		{} as Record<K, IPropActions>
	);

export const globalType = generateActionTypes(['notificationGlobal', 'sessionTimeoutGlobal']);
export const authType = generateActionTypesNew('auth', [
	'login',
	'register',
	'userFacingLogin',
	'otpLogin',
	'update',
	'changePassword',
	'activityData',
	'logout',
	'logoutAllDevices',
]);
export const zoneType = generateActionTypesNew('zone', ['create', 'get', 'getById', 'getByName', 'update', 'deleteById']);
export const countryStateType = generateActionTypesNew('state', [
	'create',
	'get',
	'getWards',
	'getByID',
	'getByCode',
	'getByName',
	'update',
	'deleteById',
	'updateMetaData',
]);
export const lgaType = generateActionTypesNew('lga', [
	'get',
	'getById',
	'create',
	'update',
	'deleteById',
	'getByName',
	'getInState',
	'getByCode',
	'updateMetaData',
]);
export const electionCycleType = generateActionTypesNew('electioncycle', [
	'create',
	'get',
	'getByID',
	'getByName',
	'update',
	'deleteById',
]);
export const electionTypeType = generateActionTypesNew('electiontype', ['create', 'get', 'getByID', 'updateByID', 'deleteByID']);
export const electionOfficialType = generateActionTypesNew('electionofficial', [
	'create',
	'createBulk',
	'get',
	'getById',
	'update',
	'deleteById',
	'updateStatus',
	'filterBy',
]);
export const electionType = generateActionTypesNew('election', [
	'create',
	'getByID',
	'get',
	'getCustom',
	'getByName',
	'update',
	'deleteById',
	'getPollingUnits',
	'getIREV',
	'updateIREVReference',
]);
export const resultAnalyticsType = generateActionTypesNew('resultanalytics', [
	'get',
	'deleteById',
	'getStats',
	'getResultChart',
	'checkEntity',
]);
export const electiveCategoryType = generateActionTypesNew('electivecategory', [
	'create',
	'get',
	'getByID',
	'getByName',
	'update',
	'deleteById',
]);
export const constituencyType = generateActionTypesNew('constituency', [
	'create',
	'get',
	'getByID',
	'getByName',
	'update',
	'deleteById',
	'createLGA',
	'getLGA',
	'getLGAIn',
	'createWard',
	'getWard',
	// 'getWardByID',
	'updateWard',
	'getWards',
	'getByStateId',
	'updateMetaData',
	'getLGAFor',
	'getForWard',
	'mapLGA',
	'mapWard',
]);
export const actionType = generateActionTypesNew('action', [
	'create',
	'get',
	'update',
	'getByID',
	'unassignMultipleForRoles',
	'getForRole',
	'getForMultipleRoles',
	// B2.10: createRole/getRole/deleteRole removed — they targeted /api/v1/ActionRole
	// which was consolidated into RoleController bulk endpoints.
	'getRoleByID',
	'deleteByActionIds',
	'getRoles',
	'assignMultipleRoles',
	'unassignMultipleRoles',
]);
export const candidateType = generateActionTypesNew('candidate', [
	'create',
	'get',
	'getByID',
	'getCandidateElection',
	'getActiveElections',
	'update',
	'deleteById',
]);
export const collationOfficerType = generateActionTypesNew('collationofficer', [
	'create',
	'get',
	'getByID',
	'update',
	'deleteById',
]);
export const wardType = generateActionTypesNew('ward', [
	'create',
	'get',
	'getByID',
	'getByCode',
	'getInLGA',
	'update',
	'deleteById',
	'updateMetaData',
]);

export const userType = generateActionTypes([
	'get_User',
	'get_UserByID',
	'get_UserByEmail',
	'get_UserByPhoneNumber',
	'get_UserRoles',
	'delete_User',
	'add_UserToRole',
	'remove_UserFromRole',
	'activate_User',
	'deActivate_User',
]);

export const auditTrailType = generateActionTypes(['get_AuditTrailUser', 'get_AuditTrailAll']);

export const roleType = generateActionTypes([
	'get_Role',
	'create_Role',
	'delete_Role',
	'getUserIn_Role',
	'get_RoleByName',
	'update_Role',
	'assignMultipleAction_Role',
	'unassignMultipleAction_Role',
]);

export const presidingOfficerType = generateActionTypes([
	'create_PresidingOfficer',
	'get_PresidingOfficer',
	'get_PresidingOfficerPollingUnit',
	'get_PresidingOfficerByID',
	'update_PresidingOfficer',
	'delete_PresidingOfficer',
]);

export const poolingUnitType = generateActionTypes([
	'create_PoolingUnit',
	'get_PoolingUnit',
	'get_PoolingUnitByID',
	'get_PoolingUnitByName',
	'get_PoolingUnitByCode',
	'update_PoolingUnit',
	'get_PoolingUnitPresidingOfficer',
	'get_PoolingUnitInWard',
	'delete_PoolingUnit',
	'updateMetaData_PoolingUnit',
]);

export const partyType = generateActionTypes([
	'create_Party',
	'get_Party',
	'get_PartyByID',
	'get_PartyByName',
	'update_Party',
	'delete_Party',
	'generate_PartyRecruitmentLink',
	'get_PartyRecruitmentLink',
]);

export const geozoneType = generateActionTypes([
	'create_GeographicalZone',
	'get_GeographicalZone',
	'get_GeographicalZoneByID',
	'get_GeographicalZoneByName',
	'update_GeographicalZone',
	'delete_GeographicalZone',
]);

export const quickactionsType = generateActionTypes(['create_QuickActions', 'get_QuickActions']);

export const resultType = generateActionTypes([
	'create_Result',
	'get_Result',
	'upload_Result',
	'push_ResultData',
	'push_ResultDataModel',
	'seed_Results',
	'update_ResultStatus',
	'update_ResultData',
	'extract_ResultFile',
	'send_ResultEmail',
	'check_ResultRequest',
	'extract_ResultUrl',
	'add_ResultFlag',
	'get_ResultFlag',
	'get_ResultFlagByID',
	'update_ResultFlagByID',
	'delete_ResultFlagByID',
	'get_ResultStatsByDataSource',
	'get_ResultStatsByDataSourceWard',
	'get_ResultStatsByDataSourceLga',
	'get_ResultStatsByDataSourceState',
]);

export const irevType = generateActionTypes([
	'push_IRevWardDataModel',
	'get_IRevWardDataModel',
	'get_IRevWardDataModelByID',
	'get_IRevStateDataModel',
	'get_IRevStateDataModelByID',
	'get_IRevLGADataModel',
	'get_IRevLGADataModelByID',
	'upload_IRevResult',
	'push_IRevLGADataModel',
	'push_IRevStateDataModel',
	'push_IRevPollingUnitDataModel',
	'push_IRevPollingUnitDataExcelResult',
	'addNote_IRevPollingUnitDataModel',
	'addNote_IRevWardDataModel',
	'addNote_IRevLGADataModel',
	'addNote_IRevStateDataModel',
	'deleteRequest_IRevPollingUnitDataModel',
	'approveDeleteRequest_IRevPollingUnitDataModel',
	'approveDeleteRequest_IRevWardDataModel',
	'deleteRequest_IRevWardDataModel',
	'approveDeleteRequest_IRevLGADataModel',
	'deleteRequest_IRevLGADataModel',
	'deleteRequest_IRevStateDataModel',
	'approveDeleteRequest_IRevStateDataModel',
	'get_IRevDownloadRequest',
	'get_IRevPollingUnitDataModel',
	'get_IRevPollingUnitDataModelByID',
	'get_IRevPollingUnitByRefId',
	'get_IRevWardByRefId',
	'get_IRevLGAByRefId',
	'get_IRevStateByRefId',
	'get_IRevPollingUnitById',
	'get_IRevWardById',
	'get_IRevLGAById',
	'get_IRevStateById',
	'get_IRevResultAnalyticsStats',
	'get_IRevResultAnalyticsForElection',
	'get_IRevResultAnalyticsChart',
	'get_IRevResultAnalyticsChartCompare',
]);

export const reportType = generateActionTypes([
	'create_Report',
	'get_Report',
	'get_ReportByID',
	'get_ReportMarkedAsResolved',
	'update_ReportByID',
]);

export const searchType = generateActionTypes(['get_SearchElection']);

export const volunteerType = generateActionTypes([
	'create_Volunteer',
	'get_Volunteer',
	'get_VolunteerByID',
	'approve_Volunteer',
	'reject_Volunteer',
	'delete_Volunteer',
	'get_VolunteerDashboardData',
	'create_VolunteerStatus',
	'get_VolunteerStatus',
	'get_VolunteerStatusById',
	'get_VolunteerStats',
	'get_VolunteerRightSection',
]);

export const notificationType = generateActionTypes(['getAll_Notification', 'delete_Notification', 'get_NotificationByID']);

export const subscriptionType = generateActionTypes([
	'create_Subscription',
	'get_Subscription',
	'get_SubscriptionById',
	'get_SubscriptionDocumentTypeEmails',
	'unsubscribe_Subscription',
	'addEmails_Subscription',
	'get_SubscriptionType',
	'get_SubscriptionTypeById',
	'get_SubscriptionByElection',
	'removeEmails_Subscription',
]);

export const aggregationType = generateActionTypes([
	'pollingunit_aggregation',
	'pollingunit_aggregationsummaries',
	'pollingunit_aggregationstats',
	'pollingunit_aggregationvisualization',
	'ward_aggregation',
	'ward_aggregationsummaries',
	'ward_aggregationstats',
	'ward_aggregationvisualization',
	'lga_aggregation',
	'lga_aggregationsummaries',
	'lga_aggregationstats',
	'lga_aggregationvisualization',
	'state_aggregation',
	'state_aggregationsummaries',
	'state_aggregationstats',
	'state_aggregationvisualization',
]);
